import { useEffect, useState } from 'react';
import { store, useStore } from '@/state/store';
import { generateDesigns, getImage, redesign } from '@/engine/pipeline';
import { renderThumbnail } from '@/engine/render/exporter';
import { RECIPE_LABELS } from '@/engine/layout/styles';
import { getFormat } from '@/lib/formats';

/**
 * The variant strip. Thumbnails are rendered through the same renderer as the
 * main canvas, so what a thumbnail shows is exactly what selecting it produces.
 */
export function AIDesignGenerator() {
  const variants = useStore((s) => s.variants);
  const activeId = useStore((s) => s.activeId);
  const status = useStore((s) => s.status);
  const photo = useStore((s) => s.photoDataUrl);
  const title = useStore((s) => s.title);
  const logoUrl = useStore((s) => s.logoDataUrl);
  const provider = useStore((s) => s.usedProvider);
  const format = useStore((s) => s.format);

  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const busy = status === 'analyzing' || status === 'generating';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (variants.length === 0) {
        setThumbs({});
        return;
      }
      const photoImg = photo ? await getImage(photo) : null;
      const logoImg = logoUrl ? await getImage(logoUrl) : null;
      const next: Record<string, string> = {};
      for (const variant of variants) {
        next[variant.id] = await renderThumbnail(variant, photoImg, logoImg);
      }
      if (!cancelled) setThumbs(next);
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [variants, photo, logoUrl]);

  const canGenerate = Boolean(photo) && title.trim().length > 0 && !busy;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={!canGenerate}
          onClick={() => void generateDesigns()}
        >
          {busy ? (
            <>
              <Spinner />
              {status === 'analyzing' ? 'Analyzing…' : 'Composing…'}
            </>
          ) : variants.length ? (
            'Regenerate'
          ) : (
            'Generate designs'
          )}
        </button>
        <button
          type="button"
          className="btn-ghost"
          disabled={variants.length === 0 || busy}
          onClick={() => void redesign()}
          title="New compositions from the same image analysis — no second API call"
        >
          AI Redesign
        </button>
      </div>

      {variants.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {variants.map((variant) => (
            <button
              key={variant.id}
              type="button"
              onClick={() => store.set({ activeId: variant.id, selectedBlockId: null })}
              className={`group overflow-hidden rounded-lg border text-left transition ${
                activeId === variant.id
                  ? 'border-accent ring-1 ring-accent/40'
                  : 'border-white/[0.07] hover:border-white/20'
              }`}
              title={variant.rationale.join('\n')}
            >
              {/* Match the real output ratio so a thumbnail is a true preview
                  rather than a crop of one. */}
              <div
                className="flex items-center justify-center bg-ink-850"
                style={{
                  aspectRatio: `${getFormat(format).width} / ${getFormat(format).height}`,
                  // Story is 9:16; without a cap the strip would push the
                  // canvas — the thing being edited — out of view.
                  maxHeight: 132,
                }}
              >
                {thumbs[variant.id] ? (
                  <img
                    src={thumbs[variant.id]}
                    alt={variant.name}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Spinner />
                )}
              </div>
              <div className="px-1.5 py-1">
                <p className="truncate text-[10px] text-ink-200">{variant.name}</p>
                <p className="truncate text-[9px] text-ink-500">
                  {RECIPE_LABELS[variant.recipe]}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {provider && variants.length > 0 && (
        <p className="text-[10px] text-ink-500">Composed by {provider}.</p>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
    />
  );
}
